/**
 * Pure JSON-LD-Builder für die SEO-Auszeichnung der Content-Seiten
 * (News, Live-Blog, PDC-Turnierseiten). Alle Builder sind fail-soft:
 * fehlende Felder werden weggelassen statt kaputte Strukturen zu erzeugen.
 */

export const SITE_ORIGIN = 'https://dartcloudapp.de';
export const SITE_NAME = 'DartCloud';
export const SITE_LOGO = `${SITE_ORIGIN}/logo512.png`;

const clean = <T extends Record<string, unknown>>(obj: T): T => {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value == null) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    out[key] = value;
  }
  return out as T;
};

export interface BreadcrumbItem {
  name: string;
  path?: string; // relativer Pfad, letztes Element ohne
}

export function buildBreadcrumbJsonLd(items: BreadcrumbItem[]): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, idx) =>
      clean({
        '@type': 'ListItem',
        position: idx + 1,
        name: item.name,
        item: item.path ? `${SITE_ORIGIN}${item.path}` : undefined,
      })
    ),
  };
}

export interface FaqEntry {
  question?: string | null;
  answer?: string | null;
}

/** FAQPage-Auszeichnung — nur Einträge mit Frage UND Antwort. */
export function buildFaqJsonLd(faqs: FaqEntry[]): object | null {
  const valid = (faqs || []).filter(
    (f) => !!f.question?.trim() && !!f.answer?.trim()
  );
  if (valid.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: valid.map((f) => ({
      '@type': 'Question',
      name: f.question!.trim(),
      acceptedAnswer: { '@type': 'Answer', text: f.answer!.trim() },
    })),
  };
}

export interface SportsEventInput {
  name?: string | null;
  description?: string | null;
  startDate?: number | null; // epoch ms
  endDate?: number | null;
  venue?: string | null;
  city?: string | null;
  country?: string | null;
  image?: string | null;
  path?: string | null;
  status?: string | null; // UPCOMING | LIVE | COMPLETED | CANCELLED
  organizer?: string | null;
}

/** SportsEvent-Auszeichnung für PDC-Turnierseiten. */
export function buildSportsEventJsonLd(input: SportsEventInput): object | null {
  if (!input.name) return null;
  const iso = (ms?: number | null) =>
    typeof ms === 'number' && Number.isFinite(ms)
      ? new Date(ms).toISOString()
      : undefined;

  const location =
    input.venue || input.city
      ? clean({
          '@type': 'Place',
          name: input.venue ?? input.city ?? undefined,
          address: clean({
            '@type': 'PostalAddress',
            addressLocality: input.city ?? undefined,
            addressCountry: input.country ?? undefined,
          }),
        })
      : undefined;

  return clean({
    '@context': 'https://schema.org',
    '@type': 'SportsEvent',
    name: input.name,
    sport: 'Darts',
    description: input.description ?? undefined,
    startDate: iso(input.startDate),
    endDate: iso(input.endDate),
    eventStatus:
      input.status === 'CANCELLED'
        ? 'https://schema.org/EventCancelled'
        : 'https://schema.org/EventScheduled',
    location,
    image: input.image ?? undefined,
    url: input.path ? `${SITE_ORIGIN}${input.path}` : undefined,
    organizer: input.organizer
      ? { '@type': 'Organization', name: input.organizer }
      : undefined,
  });
}

export interface LiveBlogInput {
  title?: string | null;
  path?: string | null; // Seite, auf der der Blog läuft
  status?: string | null; // 'live' | 'finished'
  createdAt?: number | string | null;
  entries?: Array<{
    title?: string | null;
    content?: string | null;
    createdAt?: number | string | null;
  }> | null;
}

/** LiveBlogPosting-Auszeichnung (Google Live-Blog Rich Results). */
export function buildLiveBlogJsonLd(input: LiveBlogInput): object | null {
  const entries = (input.entries || []).filter(
    (e) => !!(e.title || e.content)
  );
  if (!input.title || entries.length === 0) return null;
  const iso = (v?: number | string | null) => {
    if (v == null) return undefined;
    const d = typeof v === 'number' ? new Date(v) : new Date(v);
    return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
  };
  return clean({
    '@context': 'https://schema.org',
    '@type': 'LiveBlogPosting',
    headline: input.title,
    url: input.path ? `${SITE_ORIGIN}${input.path}` : undefined,
    coverageStartTime: iso(input.createdAt),
    coverageEndTime:
      input.status === 'finished'
        ? iso(entries[0]?.createdAt) ?? undefined
        : undefined,
    liveBlogUpdate: entries.slice(0, 50).map((e) =>
      clean({
        '@type': 'BlogPosting',
        headline: e.title ?? undefined,
        articleBody: e.content ?? undefined,
        datePublished: iso(e.createdAt),
      })
    ),
  });
}
